import { rem } from '@mantine/core';

import {
  ActivityThemeStyles,
  BackgroundProps,
  BorderProps,
  BorderRadiusProps,
  ButtonStyle,
  QuestionNumberingStyle,
  BulletStyle,
  BulletType,
  TitleStyle
} from '../../common/themes/theme';

const contentFont = 'var(--font-almoni)';
const systemFont = 'var(--font-almoni)';
const secondarySystemFont = 'var(--font-openSansHebrew)';

const defaultBorder: BorderProps = {
  borderColor: '#a6a7ab',
  borderWidth: 0,
  borderWidthLeft: 0,
  borderWidthRight: 0,
  borderWidthTop: 0,
  borderWidthBottom: 0,
  borderStyle: 'solid',
  isSplit: false
};

const defaultBorderRadius: BorderRadiusProps = {
  radius: 0,
  topLeft: 0,
  topRight: 0,
  bottomRight: 0,
  bottomLeft: 0,
  isSplit: false
};

const defaultBackgroundImage: Omit<BackgroundProps, 'backgroundColor'> = {
  backgroundImage: '',
  backgroundRepeat: 'no-repeat',
  backgroundSize: 'cover',
  backgroundPosition: 'left top'
};

export const mockedDefaultActivityThemeStyles: ActivityThemeStyles = {
  colors: {
    system: {
      primary: '#5750FA',
      secondary: '#ECEBFF'
    },
    content: {
      primary: '#1098AD',
      secondary: '#D5ECED',
      additionalColors: [],
      deletedAdditionalColors: []
    },
    neutral: {
      white: '#FFFFFF',
      gray1: '#F1F3F5',
      gray2: '#DEE2E6',
      gray3: '#ADB5BD',
      black: '#3E3A3A'
    }
  },
  fonts: {
    title: {
      fontFamily: contentFont,
      color: '#000000',
      fontSize: 36,
      fontWeight: 'bold',
      lineHeight: 1.15,
      letterSpacing: 0
    },
    headline: {
      fontFamily: contentFont,
      color: '#000000',
      fontSize: 32,
      fontWeight: 'bold',
      lineHeight: 1.15,
      letterSpacing: 0
    },
    subHeadline: {
      fontFamily: contentFont,
      color: '#000000',
      fontSize: 28,
      fontWeight: 'bold',
      lineHeight: 1.15,
      letterSpacing: 0
    },
    bodyText: {
      fontFamily: contentFont,
      color: '#000000',
      fontSize: 20,
      fontWeight: 'normal',
      lineHeight: 1.5,
      letterSpacing: 0
    }
  },
  backgrounds: {
    page: {
      backgroundColor: '#f1f3f5',
      ...defaultBackgroundImage
    },
    workingArea: {
      regular: {
        backgroundColor: '#ffffff',
        ...defaultBackgroundImage,
        border: defaultBorder,
        borderRadius: defaultBorderRadius
      },
      start: {
        backgroundColor: '#ffffff',
        ...defaultBackgroundImage,
        border: defaultBorder,
        borderRadius: defaultBorderRadius
      },
      end: {
        backgroundColor: '#ffffff00',
        ...defaultBackgroundImage,
        border: defaultBorder,
        borderRadius: defaultBorderRadius
      }
    }
  },
  cardElement: {
    backgroundColor: '#FFFFFF',
    blockSpacing: 24,
    paddingBlock: {
      padding: 32,
      top: 32,
      bottom: 32,
      isSplit: false
    },
    paddingInline: {
      padding: 32,
      left: 32,
      right: 32,
      isSplit: false
    },
    border: {
      borderColor: '#DFDFDF',
      borderWidth: 0,
      borderWidthLeft: 0,
      borderWidthRight: 0,
      borderWidthTop: 0,
      borderWidthBottom: 0,
      borderStyle: 'solid',
      isSplit: false
    },
    borderRadius: {
      radius: 24,
      topLeft: 24,
      topRight: 24,
      bottomRight: 24,
      bottomLeft: 24,
      isSplit: false
    },
    shadow: {
      type: 'soft',
      soft: {
        value: '0px 0px 14px 0px',
        color: '#343A401A'
      },
      strong: {
        value: '0px 0px 5px 0px',
        color: '#5E5D7E4D'
      },
      hard: {
        value: '8px 8px 0px 0px',
        color: '#343A40'
      }
    }
  },
  questions: {
    wrapAsCard: false,
    buttons: {
      main: {
        style: ButtonStyle.Primary,
        borderRadius: {
          radius: 8,
          topLeft: 8,
          topRight: 8,
          bottomRight: 8,
          bottomLeft: 8,
          isSplit: false
        }
      },
      helper: {
        style: ButtonStyle.Secondary,
        borderRadius: {
          radius: 8,
          topLeft: 8,
          topRight: 8,
          bottomRight: 8,
          bottomLeft: 8,
          isSplit: false
        }
      }
    },
    titles: {
      title: {
        style: TitleStyle.Regular,
        color: '#1098AD',
        numbering: QuestionNumberingStyle.BeforeTitleWithDot
      },
      bullet: {
        type: BulletType.Number,
        color: '#1098AD',
        number: BulletStyle.CircleFilled,
        letter: BulletStyle.CircleFilled,
        icon: 'bullet1'
      }
    }
  },
  stage: {
    minQuestionInputHeight: rem(96),
    questionInputWrapperPadding: rem(12),
    questionInputWrapperPaddingSum: rem(24),
    navigationButtonFontFamily: contentFont
  },
  topbar: {
    backgroundColor: '#343A40',
    color: '#ECEBFF',

    burger: {
      backgroundColor: '#FDFDFD'
    },
    avatar: {
      backgroundColor: '#1098AD',
      color: '#ffffff'
    },
    viewModeDropdown: {
      color: '#ffffff'
    },
    actionIcon: {
      standard: {
        color: '#FDFDFD',
        backgroundColor: '#343A40'
      },
      disabled: {
        color: '#ADB5BD',
        backgroundColor: '#343A40'
      }
    },
    saveButton: {
      standard: {
        backgroundColor: '#5750FA',
        color: '#FDFDFD'
      },
      hovered: {
        backgroundColor: '#748FFC',
        color: '#FDFDFD'
      },
      disabled: {
        backgroundColor: '#495057',
        color: '#ADB5BD'
      }
    },
    publishButton: {
      standard: {
        backgroundColor: '#343A40',
        color: '#F1F3F5'
      },
      hovered: {
        backgroundColor: '#495057',
        color: '#F1F3F5'
      },
      disabled: {
        backgroundColor: '#343A40',
        color: '#868E96'
      }
    },
    player: {
      backgroundColor: '#5750FA',
      color: '#ffffff',

      activeMenu: {
        backgroundColor: '#8D83FB'
      },
      loginButton: {
        standard: {
          color: '#ffffff',
          backgroundColor: '#5750fa',
          borderRadius: 8,
          borderColor: '#ffffff',
          borderWidth: 0,
          borderStyle: 'solid'
        },

        hovered: {
          color: '#ffffff',
          backgroundColor: '#8D83FB',
          borderRadius: 8,
          borderColor: '#8D83FB',
          borderWidth: 0,
          borderStyle: 'solid'
        }
      },
      actionIcon: {
        standard: {
          color: '#ffffff',
          backgroundColor: '#5750fa',
          borderRadius: 8,
          borderColor: '#ffffff',
          borderWidth: 0,
          borderStyle: 'solid'
        },

        hovered: {
          color: '#ffffff',
          backgroundColor: '#332EA9CC',
          borderRadius: 8,
          borderColor: '#8D83FB',
          borderWidth: 0,
          borderStyle: 'solid'
        }
      }
    }
  },
  system: {
    fontFamily: systemFont,
    secondaryFontFamily: secondarySystemFont
  }
};
